/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 6 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import React, { useState, forwardRef, useCallback, useMemo, useId, useRef, useEffect } from "react";
import { untranslated, type TranslatedString } from "@i18n";
import {
  MenuItem,
  ListItemText,
  SxProps,
  Theme,
  CircularProgress,
  Popper,
  ClickAwayListener,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { cssLengthToPx } from "@CubeComponents/utils/cssLengthToPx";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { AutoSizer, List } from "react-virtualized";
import axiosWrapper from "../../services/axiosWrapper";
import TextFieldComponent from "./TextFieldComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import Text from "@CubeComponents/html/Text/Text";
import ToolTipComponent from "@CubeComponents/html/tooltip";

// ===== Type Definitions =====

/** Base constraint for option objects - must be a plain object with string keys */
type OptionBase = Record<string, unknown>;

/** Configuration for mapping option object keys to display text and unique value */
interface SelectFieldsConfig {
  text: string;
  value: string;
}

/** Individual where clause for remote data query filtering */
interface QueryWhereClause {
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
  field?: string;
  operator?: string;
  value?: unknown;
}

/** Query parameters sent to remote data source */
interface RemoteDataQuery {
  where?: QueryWhereClause[];
  [key: string]: unknown;
}

/** Shape of the response from remote data source */
interface RemoteDataResponse {
  data: { result: unknown[] };
}

/** Arguments passed to beforeOpen callback in action menu mode */
interface BeforeOpenArgs {
  items: ActionMenuItem[];
}

/** Syncfusion-compatible item type for action menus */
interface ActionMenuItem {
  [key: string]: unknown;
  id?: string;
  text?: string;
  value?: string | number;
  iconCss?: string;
  separator?: boolean;
  disabled?: boolean;
}

/** Syncfusion-compatible event args for select callback */
interface SelectEventArgs {
  item: {
    properties: ActionMenuItem;
    id?: string;
    text?: string;
  };
  element?: HTMLElement;
  /** Set to true inside the callback to cancel the selection */
  cancel?: boolean;
}

interface SelectDropDownProps<T extends OptionBase = OptionBase> {
  // Core data props
  options?: T[];
  value?: T | T[] | null;
  onChange?: (value: T | T[] | null, args?: { cancel: boolean }) => void | Promise<void>;
  beforeDataBound?: (response: RemoteDataResponse, query: RemoteDataQuery) => T[];
  displayTemplate?: (displayValue: React.ReactNode) => React.ReactNode;
  /** Placeholder text - must be wrapped in t() for internationalization */
  placeholder?: TranslatedString | React.ReactNode;
  multiple?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  showNoOptions?: boolean;
  error?: string;
  remoteDataSource?: string;
  query?: RemoteDataQuery;
  helperText?: string;
  size?: "small" | "medium" | "large";
  maxHeight?: number | string;
  width?: string | number;
  sx?: SxProps<Theme>;
  menuSx?: SxProps<Theme>;
  buttonType?: string;
  persistPlaceholder?: boolean;
  fields?: SelectFieldsConfig;
  menuItemSx?: SxProps<Theme>;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  startIcon?: React.ReactNode;

  clearable?: boolean;
  searchable?: boolean;
  virtualizeList?: boolean;
  groupBy?: (option: T) => string;
  buttonWidth?: string;
  headerTemplate?: () => React.ReactNode;
  footerTemplate?: () => React.ReactNode;
  emptyListTemplate?: () => React.ReactNode;

  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  popupAlign?: "left" | "right" | "center";
  popperWidth?: number | string;
  disableClickAwayListener?: boolean;
  valueTemplate?: (matchedOption: T | undefined) => React.ReactNode;

  // Syncfusion DropDownButtonComponent compatibility props
  children?: React.ReactNode;
  items?: ActionMenuItem[];
  select?: (args: SelectEventArgs) => void;
  onSelect?: (args: SelectEventArgs) => void;
  cssClass?: string;
  className?: string;
  hideCaretIcon?: boolean;
  content?: string;
  iconCss?: string;
  onClick?: (e: React.MouseEvent) => void;
  "data-testid"?: string;
  // Additional style props for compatibility
  style?: React.CSSProperties;
  bgColor?: string;
  // Legacy Syncfusion compatibility props
  allowButtonPadding?: boolean;
  popUpWidth?: string;
  maxPopUpHeight?: string;
  beforeOpen?: (args: BeforeOpenArgs) => void;
}

function SelectDropDownComponent<T extends OptionBase = OptionBase>(
  props: SelectDropDownProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
): React.ReactElement {
  const {
    options = null,
    value,
    onChange,
    displayTemplate,
    placeholder,
    headerTemplate,
    footerTemplate,
    remoteDataSource,
    showNoOptions = true,
    beforeDataBound,
    query,
    multiple = false,
    isLoading = false,
    disabled = false,
    error,
    helperText,
    maxHeight: maxHeightProp,
    sx,
    buttonType,
    menuSx,
    renderOption,
    startIcon,
    clearable = false,
    searchable = false,
    virtualizeList = false,
    groupBy,
    id,
    fields,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    buttonWidth,
    popupAlign = "left",
    popperWidth: popperWidthProp,
    disableClickAwayListener,
    persistPlaceholder,
    menuItemSx,
    valueTemplate,
    // Syncfusion compatibility props
    children,
    items,
    select,
    onSelect,
    cssClass = "",
    className = "",
    hideCaretIcon,
    // content,
    onClick,
    "data-testid": dataTestId,
    style,
    bgColor,
    // Legacy Syncfusion compatibility props
    popUpWidth,
    maxPopUpHeight,
    beforeOpen,
    emptyListTemplate,
  } = props;

  // Map legacy props to their correct counterparts
  const popperWidth = popperWidthProp || popUpWidth;
  const maxHeight =
    maxHeightProp ??
    (maxPopUpHeight
      ? Number.isFinite(cssLengthToPx(maxPopUpHeight))
        ? cssLengthToPx(maxPopUpHeight)
        : cssLengthToPx("18.75rem")
      : cssLengthToPx("18.75rem"));

  // Determine if this is being used as an action menu (Syncfusion-style)
  const isActionMenu = Boolean(children || items);

  // Check if caret should be hidden (via cssClass containing e-caret-hide or explicit prop)
  const shouldHideCaret = hideCaretIcon || cssClass.includes("e-caret-hide");

  // Use items if provided, otherwise use options
  const effectiveOptions: OptionBase[] | null = items || options;

  // Default fields for action menu items

  const effectiveFields: SelectFieldsConfig = fields || { text: "text", value: "id" };
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const open = Boolean(anchorEl);
  const [optionsState, setOptionsState] = useState<OptionBase[]>([]);
  const optionId = effectiveFields.value;
  const generatedId = useId();
  const uniqueId = useMemo(() => id || `select-dropdown-${generatedId}`, [id, generatedId]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Call custom onClick handler if provided
    if (onClick) {
      onClick(event);
    }

    if (!disabled) {
      if (anchorEl) {
        // Currently open, close it
        setAnchorEl(null);
      } else {
        // About to open - call beforeOpen callback to allow item modification
        if (beforeOpen && isActionMenu) {
          const itemsCopy = optionsState.map((item) => ({ ...item })) as ActionMenuItem[];
          beforeOpen({ items: itemsCopy });
          setOptionsState(itemsCopy);
        }
        setAnchorEl(event.currentTarget);
      }
    }
  };

  useEffect(() => {
    try {
      if (Array.isArray(effectiveOptions)) {
        setOptionsState(effectiveOptions);
      } else if (!remoteDataSource && !query) {
        // Only throw error if not an action menu (action menus may have empty items)
        if (!isActionMenu) {
          throw new Error("options should be an array or use remote data source");
        }
      }
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  }, [effectiveOptions, isActionMenu]);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchOptions = async (queryParam: RemoteDataQuery | undefined = query) => {
    let optionsData: OptionBase[];
    if (!queryParam || !remoteDataSource) return;
    setLoading(true);
    // Dynamic remoteDataSource URL - endpoint not known at compile time
    const response = await axiosWrapper.post<{ result: OptionBase[] }>(remoteDataSource, {
      value: { ...queryParam, params: queryParam },
    });
    if (!mountedRef.current) return;
    if (beforeDataBound) {
      optionsData = beforeDataBound(response as RemoteDataResponse, queryParam);
    } else {
      optionsData = response.data.result;
    }
    // A dynamic remoteDataSource (or a custom beforeDataBound) can yield a
    // non-array payload (missing/empty `result`). optionsState is relied upon
    // throughout this component as an array, so normalise at the ingestion
    // boundary instead of guarding every consumer.
    setOptionsState(Array.isArray(optionsData) ? optionsData : []);
    setLoading(false);
  };

  useEffect(() => {
    fetchOptions();
  }, [query]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  const standbyRef = useRef<HTMLElement>(null);

  const buttonRef = ref || standbyRef;

  const handleSelect = async (option: OptionBase, element?: HTMLElement) => {
    if (option.disabled) return;

    // For action menu mode (Syncfusion compatibility)
    if (isActionMenu) {
      const menuItem = option as ActionMenuItem;
      const selectArgs: SelectEventArgs = {
        item: {
          properties: menuItem,
          id: menuItem.id,
          text: menuItem.text,
        },
        element,
        cancel: false,
      };

      // Call Syncfusion-compatible callbacks - either may set cancel=true to abort
      if (select) {
        select(selectArgs);
      }
      if (onSelect) {
        onSelect(selectArgs);
      }

      if (selectArgs.cancel) return;

      handleClose();
      return;
    }

    // Standard select dropdown mode
    if (multiple) {
      const currentValues: T[] = Array.isArray(value) ? value : [];
      const alreadySelected = currentValues.some((v) => v[optionId] === option[optionId]);
      const newValue: T[] = alreadySelected
        ? currentValues.filter((v) => v[optionId] !== option[optionId])
        : [...currentValues, option as T];
      const changeArgs = { cancel: false };
      if (onChange) await onChange(newValue, changeArgs);
      if (changeArgs.cancel) return;
    } else {
      const changeArgs = { cancel: false };
      if (onChange) await onChange(option as T, changeArgs);
      if (!value && !changeArgs.cancel) {
        const text = option?.[effectiveFields.text];
        if (text != null) {
          setDisplayValue(text as React.ReactNode);
        }
      }
      handleClose();
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    const changeArgs = { cancel: false };
    if (onChange) onChange(multiple ? ([] as unknown as T[]) : null, changeArgs);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    if (remoteDataSource) {
      const searchQuery: RemoteDataQuery = {
        ...query,
        where: [
          {
            isComplex: false,
            ignoreCase: true,
            ignoreAccent: false,
            field: effectiveFields.text,
            operator: "startswith",
            value: event.target.value,
          },
        ],
      };
      fetchOptions(searchQuery);
    }
  };

  const isSelected = useCallback(
    (option: OptionBase) => {
      if (!value) return false;
      if (Array.isArray(value)) {
        return value.some((v) => v[optionId] === option[optionId]);
      }
      return value[optionId] === option[optionId];
    },
    [value, optionId],
  );

  const [displayValue, setDisplayValue] = useState<React.ReactNode>(placeholder || t("Select an option"));

  useEffect(() => {
    // Skip display value updates for action menu mode
    if (isActionMenu) return;

    const translatedPlaceholder: React.ReactNode = placeholder || t("Select an option");

    if (!value || !optionsState.length) {
      setDisplayValue(translatedPlaceholder);
      return;
    }
    if (persistPlaceholder) {
      return;
    }

    if (Array.isArray(value)) {
      const names = value
        .map((v) => v?.[effectiveFields.text])
        .filter(Boolean)
        .join(", ");
      setDisplayValue(names || translatedPlaceholder);
    } else {
      const text = value?.[effectiveFields.text];
      if (text != null) {
        setDisplayValue(text as React.ReactNode);
      } else {
        const matched = optionsState.find((option) => option?.[optionId] === (value?.[optionId] || value));
        setDisplayValue((matched?.[effectiveFields.text] as React.ReactNode) || translatedPlaceholder);
      }
    }
  }, [value, optionsState, isActionMenu, effectiveFields]);

  const filteredOptions = useMemo(() => {
    // Filter out separator items for processing (they're rendered separately)
    const nonSeparatorOptions = optionsState.filter((option) => !option.separator);
    if (!searchText || Boolean(remoteDataSource)) return nonSeparatorOptions;
    return nonSeparatorOptions.filter((option) => {
      const optionText = option[effectiveFields.text];
      if (typeof optionText === "string") {
        const lowerSearchText = searchText.toLowerCase();
        return optionText.toLowerCase().includes(lowerSearchText);
      } else {
        return optionText === searchText;
      }
    });
  }, [optionsState, searchText, effectiveFields, remoteDataSource]);

  const groupedOptions = useMemo(() => {
    if (!groupBy) return { ungrouped: filteredOptions };

    return filteredOptions.reduce(
      (acc: Record<string, OptionBase[]>, option) => {
        const groupKey = String(option.group || groupBy(option as T) || "ungrouped");
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(option);
        return acc;
      },
      {} as Record<string, OptionBase[]>,
    );
  }, [filteredOptions, groupBy]);

  const renderMenuItem = useCallback(
    (option: OptionBase, index: number, style?: React.CSSProperties) => {
      // Handle separator items
      if (option.separator) {
        return <Divider key={`separator-${index}`} sx={{ my: "0.25rem" }} />;
      }

      const itemSelected = isSelected(option);

      if (renderOption) {
        return (
          <MenuItemComponent
            key={(option[optionId] as React.Key) || `item-${index}`}
            selected={itemSelected}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleSelect(option, e.currentTarget as HTMLElement);
            }}
            disabled={option.disabled as boolean | undefined}
            style={style}
            sx={{ opacity: option.disabled ? 0.5 : 1, ...menuItemSx }}
          >
            {renderOption(option as T, itemSelected)}
          </MenuItemComponent>
        );
      }

      return (
        <MenuItemComponent
          key={(option[optionId] as React.Key) || `item-${index}`}
          selected={itemSelected}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleSelect(option, e.currentTarget as HTMLElement);
          }}
          disabled={option.disabled as boolean | undefined}
          style={style}
          sx={{ opacity: option.disabled ? 0.5 : 1 }}
        >
          {multiple && <CheckBoxComponent checked={itemSelected} disabled={option.disabled as boolean | undefined} />}
          <ListItemText
            primary={
              <Typography
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <ToolTipComponent
                  slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                  content={untranslated(String(option[effectiveFields.text] ?? ""))}
                >
                  {option[effectiveFields.text] as React.ReactNode}
                </ToolTipComponent>
              </Typography>
            }
          />
        </MenuItemComponent>
      );
    },
    [multiple, isSelected, renderOption, effectiveFields, handleSelect, optionId, menuItemSx],
  );

  const getPlacement = () => {
    if (popupAlign === "left") return "bottom-start";
    if (popupAlign === "right") return "bottom-end";
    return "bottom";
  };

  const getVirtualListHeight = () => {
    if (typeof maxHeight === "number") {
      const searchBarHeight = searchable ? 70 : 0;
      return maxHeight - searchBarHeight;
    }
    return 250;
  };

  const renderMenuContent = () => {
    if (loading) {
      return (
        <MenuItem sx={{ paddingBottom: "1.25rem" }} disabled>
          <CircularProgress size={20} style={{ marginInlineEnd: "0.5rem" }} />
          Loading...
        </MenuItem>
      );
    }

    if (filteredOptions?.length === 0) {
      if (emptyListTemplate) {
        return emptyListTemplate();
      }
      return (
        showNoOptions && (
          <MenuItem disabled>
            <Text>{t("No options available")}</Text>
          </MenuItem>
        )
      );
    }

    if (virtualizeList) {
      return (
        <div style={{ height: getVirtualListHeight() }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                width={width}
                rowCount={filteredOptions.length}
                rowHeight={48}
                rowRenderer={({ index, style }) => renderMenuItem(filteredOptions[index]!, index, style)}
              />
            )}
          </AutoSizer>
        </div>
      );
    }

    if (groupBy) {
      return Object.entries(groupedOptions).map(([group, groupOptions]) => (
        <React.Fragment key={group}>
          {group !== "ungrouped" && (
            <MenuItem
              disabled
              style={{
                fontWeight: "bold",
                opacity: 0.7,
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }}
            >
              {group}
            </MenuItem>
          )}
          {groupOptions.map((option, index) => renderMenuItem(option, index))}
        </React.Fragment>
      ));
    }

    // For action menus, render items including separators in original order
    if (isActionMenu && optionsState) {
      return optionsState.map((option, index) => renderMenuItem(option, index));
    }

    return filteredOptions.map((option, index) => renderMenuItem(option, index));
  };

  // Combine cssClass and className for action menu compatibility
  const combinedClassName = `${cssClass} ${className}`.trim();

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (!disableClickAwayListener) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          width: isActionMenu ? "auto" : buttonWidth ? buttonWidth : "100%",
          backgroundColor: bgColor,
          ...style,
        }}
        className={combinedClassName}
        data-testid={dataTestId}
      >
        {/* Action menu mode: children as trigger */}
        {isActionMenu && children ? (
          <button
            type="button"
            onClick={handleOpen}
            disabled={disabled}
            className="flex items-center justify-center cursor-pointer bg-transparent border-none p-0"
            style={{ opacity: disabled ? 0.5 : 1 }}
            ref={buttonRef as React.Ref<HTMLButtonElement>}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? `${uniqueId}-menu` : undefined}
          >
            {children}
          </button>
        ) : displayTemplate ? (
          <button className="w-full h-full" onClick={handleOpen}>
            {" "}
            {displayTemplate(displayValue)}{" "}
          </button>
        ) : (
          <ButtonComponent
            onClick={handleOpen}
            buttonType={buttonType}
            disabled={disabled}
            sx={{
              textAlign: "left",
              width: buttonWidth ? buttonWidth : "100%",
              justifyContent: "space-between",
              maxWidth: buttonWidth ? buttonWidth : "100%",
              minWidth: "0 !important",
              "& > span.font-semibold": {
                overflow: "hidden",
                minWidth: 0,
                flex: "1 1 0",
              },
              ...(sx || {}),
            }}
            ref={buttonRef}
            startIcon={startIcon}
            endIcon={
              shouldHideCaret ? null : (
                <>
                  {clearable && value && (value as T[])?.length !== 0 && (
                    <IconButton sx={{ padding: "0.125rem" }} onClick={handleClear}>
                      <CloseIcon
                        fontSize="inherit"
                        style={{ marginInlineEnd: 0, cursor: "pointer", alignSelf: "center", height: "100%" }}
                      />
                    </IconButton>
                  )}
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </>
              )
            }
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? `${uniqueId}-menu` : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            id={uniqueId}
          >
            {loading ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={20} style={{ marginInlineEnd: "0.5rem" }} />
                Loading...
              </div>
            ) : valueTemplate ? (
              valueTemplate(
                optionsState.find((option) => {
                  const v = value as T | null | undefined;
                  return option?.[optionId] === (v?.[optionId] || v);
                }) as T | undefined,
              )
            ) : (
              displayValue
            )}
          </ButtonComponent>
        )}

        <Popper
          id={`${uniqueId}-popper`}
          open={open}
          anchorEl={anchorEl}
          sx={{
            // For action menus, use auto width; for regular dropdowns, match button width
            width: isActionMenu ? "auto" : popperWidth || buttonWidth || "auto",
            maxWidth: isActionMenu ? "none" : popperWidth || buttonWidth || "auto",
            minWidth: isActionMenu ? "9.375rem" : popperWidth || buttonWidth || "auto",
            border: "1px solid #ECF3FE",
            boxShadow: "0.25rem 0.25rem 0.9375rem rgba(65, 92, 171, 0.15)",
            zIndex: 1500,
            background: "var(--surface-white)",
            maxHeight: `${Number(maxHeight) / 16}rem`,
            overflow: "auto",
            ...(menuSx || {}),
          }}
          placement={getPlacement()}
          modifiers={[
            {
              name: "sameWidth",
              // Disable sameWidth modifier for action menus
              enabled: !isActionMenu,
              phase: "beforeWrite",
              requires: ["computeStyles"],
              fn: ({ state }) => {
                // popperWidth may be a number, "Npx", or "Nrem"; normalize to px for popper.
                const resolved =
                  popperWidth !== undefined && popperWidth !== null
                    ? cssLengthToPx(popperWidth as string | number)
                    : NaN;
                const widthPx = Number.isFinite(resolved) ? resolved : state.rects.reference.width;
                // eslint-disable-next-line no-px/no-px -- popper width must match reference element's DOM-measured px width exactly; rem conversion risks subpixel drift if root font-size != 16px
                state.styles.popper = { ...(state.styles.popper ?? {}), width: `${widthPx}px` };
              },
            },
          ]}
        >
          <>
            {headerTemplate && <div className="py-4 sticky flex top-0 w-full z-10 bg-white">{headerTemplate()}</div>}
            {searchable && (
              <div style={{ padding: "1rem", position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                <TextFieldComponent
                  placeholder={t("Search...")}
                  size="small"
                  value={searchText}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              </div>
            )}

            {renderMenuContent()}
            {footerTemplate && <div className="py-4 flex sticky bottom-0 w-full bg-white z-10">{footerTemplate()}</div>}
          </>
        </Popper>

        {(error || helperText) && (
          <div
            style={{
              color: error ? "red" : "rgba(0, 0, 0, 0.6)",
              marginTop: "0.25rem",
              fontSize: "0.75rem",
            }}
          >
            {error || helperText}
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}

const SelectDropDown = forwardRef(SelectDropDownComponent) as <T extends OptionBase = OptionBase>(
  props: SelectDropDownProps<T> & React.RefAttributes<HTMLElement>,
) => React.ReactElement | null;

(SelectDropDown as unknown as { displayName: string }).displayName = "SelectDropDown";
export default SelectDropDown;
export type {
  SelectDropDownProps,
  ActionMenuItem,
  SelectEventArgs,
  SelectFieldsConfig,
  RemoteDataQuery,
  RemoteDataResponse,
  BeforeOpenArgs,
  OptionBase,
  QueryWhereClause,
};
